import Phaser from 'phaser';
import { ScoreService } from '../services/ScoreService';

/**
 * HighScoreEntryScene: Classic arcade-style 3-letter initials entry.
 * Use up/down arrows to change letter, left/right to move position.
 */
export class HighScoreEntryScene extends Phaser.Scene {
  private score = 0;
  private wave = 1;
  private initials: string[] = ['A', 'A', 'A'];
  private currentPosition = 0;
  private letterTexts: Phaser.GameObjects.Text[] = [];
  private arrowIndicator!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'HighScoreEntryScene' });
  }

  init(data: { score: number; wave: number }): void {
    this.score = data.score || 0;
    this.wave = data.wave || 1;
    this.initials = ['A', 'A', 'A'];
    this.currentPosition = 0;
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Title with neon glow
    this.add.text(width / 2, height * 0.15, 'NEW HIGH SCORE!', {
      fontSize: '24px',
      color: '#ffff00',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#ffff00', blur: 12, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Score display with white glow
    this.add.text(width / 2, height * 0.28, `${this.score}`, {
      fontSize: '28px',
      color: '#ffffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#aaaaff', blur: 8, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Instructions with cyan glow
    this.add.text(width / 2, height * 0.40, 'ENTER YOUR INITIALS', {
      fontSize: '12px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#00ffff', blur: 8, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);

    // Letter display
    this.letterTexts = [];
    for (let i = 0; i < 3; i++) {
      const letterText = this.add.text(width / 2 - 60 + i * 60, height * 0.55, this.initials[i], {
        fontSize: '36px',
        color: i === 0 ? '#00ffff' : '#ffffff',
        fontFamily: '"Press Start 2P", monospace',
      });
      letterText.setOrigin(0.5, 0.5);
      this.letterTexts.push(letterText);
    }

    // Arrow indicator (moves with current position)
    this.arrowIndicator = this.add.text(width / 2 - 60 + this.currentPosition * 60, height * 0.48, '▼', {
      fontSize: '14px',
      color: '#00ffff',
      fontFamily: '"Press Start 2P", monospace',
    });
    this.arrowIndicator.setOrigin(0.5, 0.5);

    // Keyboard controls (arrow keys immediately, confirm keys delayed to prevent leftover events)
    this.input.keyboard?.on('keydown-UP', () => this.changeLetterUp());
    this.input.keyboard?.on('keydown-DOWN', () => this.changeLetterDown());
    this.input.keyboard?.on('keydown-LEFT', () => this.movePositionLeft());
    this.input.keyboard?.on('keydown-RIGHT', () => this.movePositionRight());
    this.time.delayedCall(500, () => {
      if (!this.scene.isActive()) return;
      this.input.keyboard?.on('keydown-ENTER', () => this.confirmEntry());
      this.input.keyboard?.on('keydown-SPACE', () => this.confirmEntry());
    });

    // Confirm button
    const confirmBtn = this.add.text(width / 2, height * 0.75, 'CONFIRM', {
      fontSize: '16px',
      color: '#00ff41',
      fontFamily: '"Press Start 2P", monospace',
    });
    confirmBtn.setOrigin(0.5, 0.5);
    confirmBtn.setInteractive({ useHandCursor: true });
    confirmBtn.on('pointerover', () => confirmBtn.setColor('#ffffff'));
    confirmBtn.on('pointerout', () => confirmBtn.setColor('#00ff41'));
    confirmBtn.on('pointerdown', () => this.confirmEntry());

    // Help text - brighter color for better contrast
    this.add.text(width / 2, height * 0.88, '↑↓ CHANGE  ←→ MOVE  ENTER CONFIRM', {
      fontSize: '8px',
      color: '#8888aa',
      fontFamily: '"Press Start 2P", monospace',
      shadow: {
        offsetX: 0, offsetY: 0, color: '#6666aa', blur: 4, fill: true, stroke: true,
      },
    }).setOrigin(0.5, 0.5);
  }

  private changeLetterUp(): void {
    const charCode = this.initials[this.currentPosition].charCodeAt(0);
    const newChar = charCode >= 90 ? 'A' : String.fromCharCode(charCode + 1);
    this.initials[this.currentPosition] = newChar;
    this.updateDisplay();
  }

  private changeLetterDown(): void {
    const charCode = this.initials[this.currentPosition].charCodeAt(0);
    const newChar = charCode <= 65 ? 'Z' : String.fromCharCode(charCode - 1);
    this.initials[this.currentPosition] = newChar;
    this.updateDisplay();
  }

  private movePositionLeft(): void {
    this.currentPosition = Math.max(0, this.currentPosition - 1);
    this.updateDisplay();
  }

  private movePositionRight(): void {
    this.currentPosition = Math.min(2, this.currentPosition + 1);
    this.updateDisplay();
  }

  private updateDisplay(): void {
    const { width, height } = this.cameras.main;
    for (let i = 0; i < 3; i++) {
      this.letterTexts[i].setText(this.initials[i]);
      this.letterTexts[i].setColor(i === this.currentPosition ? '#00ffff' : '#ffffff');
    }
    // Move arrow indicator to current position
    this.arrowIndicator.setX(width / 2 - 60 + this.currentPosition * 60);
  }

  private confirmEntry(): void {
    const initialsStr = this.initials.join('');
    const scoreService = ScoreService.getInstance();
    scoreService.addScore(initialsStr, this.score);
    this.scene.start('HighScoreScene');
  }

  /**
   * Clean up keyboard listeners when scene is shut down.
   * Prevents memory leaks from accumulated listeners across scene transitions.
   */
  shutdown(): void {
    this.input.keyboard?.removeAllListeners();
    this.input.removeAllListeners();
    // Remove delayed timer events (confirm key delay)
    this.time.removeAllEvents();
    // Clear references to prevent GC leaks
    this.letterTexts = [];
  }
}
